<template>
  <div class="home-wrapper">
    <div class="page-background">
      <!-- Hero Section -->
      <section class="hero-section">
        <div class="hero-background">
          <v-img
            :src="require('@/assets/HERO-ANILLOTARJETA.jpg')"
            class="hero-image"
            cover
          ></v-img>
        </div>
        <v-container class="hero-content-container">
          <v-row>
            <v-col cols="12" md="6" class="hero-text-col">
              <h1 class="hero-title">
                Más ventas y clientes en <span class="text-guinda">un solo tap</span>
              </h1>
              <p class="hero-subtitle">
                Tu tarjeta o anillo abre tu perfil con un tap: te guardan, te escriben, te agendan, te piden y regresan. Tus clientes no necesitan descargar nada.
              </p>
              <v-btn 
                large 
                class="hero-cta-btn"
                outlined
                color="#68213d"
                to="/tienda"
              >
                Empieza ahora
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </section>

      <!-- Qué es NFCERO Section -->
      <section id="about-nfcero" class="about-section">
        <v-container>
          <div class="section-header text-center">
            <p class="section-pretitle">Conoce la tecnología</p>
            <h2 class="section-title">¿Qué es NFCERO?</h2>
          </div>
          
          <p class="about-description text-center mx-auto">
            NFCERO es una tarjeta o un anillo con tecnología NFC: al acercarlo a un celular, abre tu perfil digital. Ahí está todo lo que elijas:
            tu contacto, tus redes, tu agenda, tu catálogo, tu tarjeta de cliente frecuente y más. Y decides qué abre primero, sin volver a grabar la tarjeta.
          </p>

          <p class="feature-highlight text-center">
            Comparte fácilmente tus redes sociales o datos de contacto
          </p>

          <!-- Carrusel 3D Coverflow -->
          <div class="coverflow-container">
            <div class="coverflow-wrapper">
              <div 
                class="coverflow-stage"
                @mouseenter="pauseAutoplay"
                @mouseleave="resumeAutoplay"
              >
                <div 
                  v-for="(product, index) in productImages" 
                  :key="index"
                  class="coverflow-item"
                  :class="getItemClass(index)"
                  :style="getItemStyle(index)"
                  @click="goToSlide(index)"
                >
                  <v-img
                    :src="product.image"
                    class="coverflow-image"
                    aspect-ratio="1"
                    cover
                  ></v-img>
                  <p class="coverflow-caption">{{ product.text }}</p>
                </div>
              </div>
            </div>
            
            <!-- Navegación (solo dots, sin flechas) -->
            <div class="coverflow-nav">
              <div class="coverflow-dots">
                <span 
                  v-for="(_, index) in productImages" 
                  :key="index"
                  class="coverflow-dot"
                  :class="{ 'active': index === currentSlide }"
                  @click="goToSlide(index)"
                ></span>
              </div>
            </div>
          </div>
        </v-container>
      </section>

      <!-- Explora Section -->
      <section id="explore-section" class="explore-section">
        <v-container>
          <div class="section-header text-center">
            <h2 class="section-title white--text">
              Explora cómo aprovechar la tecnología <span class="text-guinda">NFCERO</span>
            </h2>
            <p class="section-subtitle white--text">
              Una solución perfecta para mejorar la relación con tus clientes y la organización dentro de tu trabajo.
            </p>
          </div>

          <div class="explore-accordion-container">
            <div 
              v-for="(pill, index) in explorePills" 
              :key="index"
              class="explore-accordion-item"
              :class="{ 'active': activePill === index }"
            >
              <!-- Header del acordeón -->
              <div class="accordion-header" @click="togglePill(index)">
                <span class="accordion-title">{{ pill.title }}</span>
                <div class="accordion-icon">
                  <v-icon color="white" small>
                    {{ activePill === index ? 'mdi-close' : 'mdi-plus' }}
                  </v-icon>
                </div>
              </div>
              
              <!-- Contenido expandido del acordeón -->
              <v-expand-transition>
                <div v-if="activePill === index" class="accordion-content">
                  <p class="accordion-intro">{{ pill.activeIntro }}</p>
                  
                  <div v-for="(module, idx) in pill.modules" :key="idx" class="accordion-module">
                    <p class="accordion-module-title">{{ module.title }}</p>
                    <p class="accordion-module-desc">{{ module.description }}</p>
                  </div>
                </div>
              </v-expand-transition>
            </div>
          </div>
        </v-container>
      </section>

      <!-- Networking en un tap Section -->
      <section class="networking-section">
        <div class="networking-background">
          <v-img
            :src="require('@/assets/banner-networking.jpg')"
            class="networking-image"
            cover
          ></v-img>
        </div>
        <v-container class="networking-content">
          <h2 class="networking-title text-center">
            Networking en un <span class="text-guinda">tap.</span>
          </h2>
        </v-container>
      </section>

      <!-- FAQs Section -->
      <section id="faqs-section" class="faqs-section">
        <v-container>
          <div class="section-header text-center">
            <p class="section-pretitle">¿Dudas?</p>
            <h2 class="section-title white--text">Estamos aquí para ayudarte</h2>
            <p class="section-subtitle white--text">
              Esperamos poder resolver tus inquietudes. Si aún tienes más preguntas, no dudes en contactarnos a través del formulario en la siguiente <a href="#contact-section" class="text-guinda-claro">sección.</a>
            </p>
          </div>

          <v-expansion-panels 
            v-model="openedFaq"
            class="faqs-panels"
            flat
          >
            <v-expansion-panel
              v-for="(faq, index) in faqs"
              :key="index"
              class="faq-panel"
            >
              <v-expansion-panel-header class="faq-header">
                {{ faq.question }}
              </v-expansion-panel-header>
              <v-expansion-panel-content class="faq-content">
                <div v-html="faq.answer"></div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-container>
      </section>

      <!-- Contact Section -->
      <section id="contact-section" class="contact-section">
        <v-container>
          <div class="section-header text-center">
            <h2 class="section-title white--text">Contáctanos</h2>
            <p class="section-subtitle white--text">
              Nos encantaría escuchar de ti, cualquier duda o comentario, no dudes en escribirnos.
            </p>
          </div>

          <v-row justify="center">
            <v-col cols="12" md="6" lg="5">
              <v-card class="contact-form-card" flat dark>
                <v-form ref="contactForm" v-model="contactFormValid" @submit.prevent="submitContactForm">
                  <v-text-field
                    v-model="contactForm.name"
                    label="Nombre"
                    outlined
                    dense
                    light
                    color="#931B4D"
                    :rules="[rules.required]"
                    class="guinda-input"
                  ></v-text-field>
                  
                  <v-text-field
                    v-model="contactForm.surname"
                    label="Apellido"
                    outlined
                    dense
                    light
                    color="#931B4D"
                    :rules="[rules.required]"
                    class="guinda-input"
                  ></v-text-field>
                  
                  <v-text-field
                    v-model="contactForm.email"
                    label="Email"
                    type="email"
                    outlined
                    dense
                    light
                    color="#931B4D"
                    :rules="[rules.required, rules.email]"
                    class="guinda-input"
                  ></v-text-field>
                  
                  <v-textarea
                    v-model="contactForm.message"
                    label="Mensaje"
                    outlined
                    dense
                    light
                    color="#931B4D"
                    rows="4"
                    :rules="[rules.required]"
                    class="guinda-input"
                  ></v-textarea>
                  
                  <v-btn
                    type="submit"
                    block
                    large
                    class="contact-submit-btn"
                    :loading="contactFormLoading"
                  >
                    Enviar mensaje
                  </v-btn>
                  <v-alert v-if="contactResult" :type="contactResult.type" dense text class="mt-3 mb-0">
                    {{ contactResult.text }}
                  </v-alert>
                </v-form>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </section>
    </div>
    <AppFooter />
  </div>
</template>

<script>
import api from '@/services/axiosInstance'

export default {
  name: 'HomeView',
  components: {
    AppFooter: () => import('@/components/AppFooter.vue')
  },
  data: () => ({
    // Carrusel 3D Coverflow
    currentSlide: 0,
    autoplayInterval: null,
    autoplayDelay: 4000,
    isAutoplayPaused: false,
    productImages: [
      { image: require('@/assets/carrusel01.jpg'), text: 'Comparte fácilmente tus redes sociales o datos de contacto.' },
      { image: require('@/assets/carrusel02.jpg'), text: 'Te agendan en tus horarios libres y reciben recordatorio para que no falten.' },
      { image: require('@/assets/carrusel03.jpg'), text: 'Envía encuestas y guarda los resultados.' },
      { image: require('@/assets/carrusel04.jpg'), text: 'Tus clientes en un solo lugar, con su seguimiento, o conecta tu Salesforce.' },
      { image: require('@/assets/carrusel05.jpg'), text: 'Comparte documentos, imágenes o videos de tus productos o servicios.' },
      { image: require('@/assets/carrusel06.jpg'), text: 'Personaliza tu herramienta a tus necesidades de networking' },
    ],
    
    // Pills de exploración
    activePill: null,
    explorePills: [
      {
        title: 'Potencia tu consultorio',
        image: require('@/assets/usecases/usecase1.png'),
        activeTitle: 'Una Solución para Médicos, Psicólogos y Veterinarios',
        activeIntro: 'Con la tarjeta digital podrás integrar módulos especializados para ti y proyectar una imagen moderna.',
        modules: [
          { title: 'Agenda sin faltas', description: 'Tus pacientes agendan en tus horarios libres, reciben recordatorio un día antes y confirman o cambian con un toque.' },
          { title: 'Reseñas y regresos', description: 'Después de la cita les pedimos su reseña en Google, y cuando ya les toca volver les recordamos agendar.' },
          { title: 'Formularios a tu medida', description: 'Registros, encuestas y solicitudes que llenan desde tu perfil.' },
          { title: 'Galería multimedia', description: 'Comparte casos de éxito, procedimientos o las instalaciones de tu consultorio.' },
          { title: 'Visor de documentos PDF', description: 'Ofrece material informativo o resultados a tus pacientes de forma profesional.' },
          { title: 'Contacto directo y redes', description: 'Integra tu WhatsApp y perfiles sociales para una comunicación fluida.' },
        ]
      },
      {
        title: 'Sector Freelancer',
        image: require('@/assets/usecases/usecase2.png'),
        activeTitle: 'De Freelancer a Referente en tu Sector',
        activeIntro: 'Tu tarjeta digital es tu mejor aliada para crecer profesionalmente y destacar en tu industria.',
        modules: [
          { title: 'Portafolio y Galería', description: 'Muestra tus mejores proyectos y diseños de forma profesional.' },
          { title: 'Captura de clientes', description: '«Déjame tus datos» en tu perfil: cada interesado entra a tu lista de clientes con su seguimiento.' },
          { title: 'Cotiza y cobra', description: 'Manda cotizaciones con IVA que tu cliente acepta desde su celular.' },
          { title: 'Networking en eventos', description: 'En una expo, quien toca tu tarjeta te deja sus datos y se lleva los tuyos; al día siguiente te recordamos escribirle.' },
          { title: 'Documentos Profesionales', description: 'Comparte tu CV, portafolio y propuestas en PDF.' },
          { title: 'Redes y Contacto', description: 'Conecta tus perfiles de LinkedIn, Behance o GitHub.' },
        ]
      },
      {
        title: 'Crea comunidad',
        image: require('@/assets/usecases/usecase3.jpg'),
        activeTitle: 'Transforma la Relación con tus Clientes',
        activeIntro: 'Tu tarjeta digital es el puente para crear una comunidad leal y comprometida.',
        modules: [
          { title: 'Cliente frecuente', description: 'Tarjeta de sellos en su celular, sin app: el sello solo se da en tu negocio y el premio llega solo.' },
          { title: 'Trae a un amigo', description: 'Quien invita y quien llega ganan un sello; y con un toque invitas a regresar a quien ya no viene.' },
          { title: 'Galerías interactivas', description: 'Muestra el detrás de cámaras, tutoriales y contenido exclusivo.' },
          { title: 'Encuestas de Satisfacción', description: 'Recopila feedback valioso de tu comunidad.' },
          { title: 'Redes Sociales', description: 'Conecta todas tus plataformas en un solo lugar.' },
        ]
      },
      {
        title: 'Impulsa tu negocio',
        image: require('@/assets/usecases/usecase4.png'),
        activeTitle: 'Una Experiencia Completa para tu Negocio',
        activeIntro: 'Lleva tu negocio al siguiente nivel con herramientas digitales profesionales.',
        modules: [
          { title: 'Catálogo y pedidos', description: 'Tus productos con foto y precio; el cliente arma su pedido y te llega por WhatsApp con el total. Tú entregas con tu repartidor o paquetería; NFCero no cobra comisión.' },
          { title: 'Sellos en el mostrador', description: 'Tu tarjeta de cliente frecuente con pantalla en el mostrador o con tus empleados, sin darles tu cuenta.' },
          { title: 'Ubicación y citas', description: 'Guía a tus clientes con el mapa y deja que agenden contigo.' },
          { title: 'Reseñas en Google', description: 'Facilita que tus clientes dejen su reseña en Google.' },
          { title: 'Tu tarjeta abre donde quieras', description: 'Tu perfil, tu WhatsApp o directo a tu catálogo; lo cambias cuando quieras.' },
        ]
      }
    ],
    
    // FAQs
    openedFaq: null,
    faqs: [
      {
        question: '¿Necesito una app especial para escanear la tarjeta o anillo?',
        answer: 'No. La mayoría de los smartphones (Android o iPhone) detectan automáticamente NFC. Solo acerca el dispositivo a la tarjeta o anillo y se abrirá el enlace.'
      },
      {
        question: '¿Mis clientes necesitan descargar una app?',
        answer: 'No. La tarjeta de sellos, los pedidos, las citas y las cotizaciones se abren en el navegador de su celular.'
      },
      {
        question: '¿Puedo cambiar lo que abre mi tarjeta sin volver a grabarla?',
        answer: 'Sí. Desde tu panel eliges si abre tu perfil completo o va directo a tu WhatsApp, tu catálogo o tu agenda (planes Premium y VIP).'
      },
      {
        question: '¿Cómo evito que alguien se ponga sellos desde su casa?',
        answer: 'El sello solo se da en tu negocio: lo das tú o tus empleados desde su celular, con una pantalla en el mostrador cuyo código cambia cada 30 segundos, o con un PIN que cambia solo.'
      },
      {
        question: '¿Por qué se abre una página de Google separada al dejar reseñas?',
        answer: 'Por seguridad, Google solo permite escribir reseñas dentro de su propio sitio. La página NFC solo te guía y te copia el texto para hacerlo más fácil.'
      },
      {
        question: '¿Pueden dejar reseñas personas sin cuenta de Google?',
        answer: 'No. Solo los usuarios con una cuenta de Google pueden dejar reseñas públicas en Maps o en los perfiles de empresas.'
      },
      {
        question: '¿Por qué algunos celulares no leen el NFC?',
        answer: `Porque tiene desactivada la función o son celulares muy viejitos. Para estos celulares se creó la opción de compartir por QR o por enlace.<br><br>
                Funciona con la gran mayoría de los smartphones modernos:<br><br>
                <strong>iPhone:</strong> Modelos XR, XS, 11, 12, 13, 14, 15 y posteriores.<br><br>
                <strong>Android:</strong> La mayoría de los modelos lanzados en los últimos 5-7 años tienen la función NFC activada por defecto.`
      },
      {
        question: '¿Qué tecnología usan las tarjetas o anillos?',
        answer: 'Las tarjetas usan chips NFC NTAG213 y los anillos NTAG216, compatibles con la mayoría de los smartphones modernos.'
      }
    ],
    
    // Formulario de contacto
    contactFormValid: false,
    contactFormLoading: false,
    contactResult: null, // { type: 'success' | 'error', text }
    contactForm: {
      name: '',
      surname: '',
      email: '',
      message: ''
    },
    rules: {
      required: v => !!v || 'Este campo es requerido',
      email: v => /.+@.+\..+/.test(v) || 'Email no válido'
    }
  }),
  mounted() {
    this.startAutoplay();
  },
  beforeDestroy() {
    this.stopAutoplay();
  },
  methods: {
    // Carrusel Coverflow
    getItemClass(index) {
      const total = this.productImages.length;
      const diff = this.getRelativePosition(index);
      
      if (diff === 0) return 'active';
      if (diff === 1 || diff === -(total - 1)) return 'next';
      if (diff === -1 || diff === (total - 1)) return 'prev';
      if (diff === 2 || diff === -(total - 2)) return 'next-next';
      if (diff === -2 || diff === (total - 2)) return 'prev-prev';
      return 'hidden';
    },
    getRelativePosition(index) {
      const total = this.productImages.length;
      let diff = index - this.currentSlide;
      
      // Normalizar para carrusel infinito
      if (diff > total / 2) diff -= total;
      if (diff < -total / 2) diff += total;
      
      return diff;
    },
    getItemStyle(index) {
      const diff = this.getRelativePosition(index);
      const total = this.productImages.length;
      
      let translateX = 0;
      let translateZ = 0;
      let rotateY = 0;
      let scale = 1;
      let opacity = 1;
      let zIndex = 10;
      
      if (diff === 0) {
        // Elemento central
        translateX = 0;
        translateZ = 100;
        rotateY = 0;
        scale = 1;
        opacity = 1;
        zIndex = 50;
      } else if (diff === 1 || diff === -(total - 1)) {
        // Siguiente
        translateX = 220;
        translateZ = 0;
        rotateY = -45;
        scale = 0.85;
        opacity = 0.8;
        zIndex = 40;
      } else if (diff === -1 || diff === (total - 1)) {
        // Anterior
        translateX = -220;
        translateZ = 0;
        rotateY = 45;
        scale = 0.85;
        opacity = 0.8;
        zIndex = 40;
      } else if (diff === 2 || diff === -(total - 2)) {
        // Siguiente-siguiente
        translateX = 380;
        translateZ = -100;
        rotateY = -60;
        scale = 0.7;
        opacity = 0.5;
        zIndex = 30;
      } else if (diff === -2 || diff === (total - 2)) {
        // Anterior-anterior
        translateX = -380;
        translateZ = -100;
        rotateY = 60;
        scale = 0.7;
        opacity = 0.5;
        zIndex = 30;
      } else {
        // Ocultos
        opacity = 0;
        zIndex = 0;
      }
      
      return {
        transform: `translateX(${translateX}px) translateZ(${translateZ}px) rotateY(${rotateY}deg) scale(${scale})`,
        opacity: opacity,
        zIndex: zIndex
      };
    },
    nextSlide() {
      this.currentSlide = (this.currentSlide + 1) % this.productImages.length;
    },
    prevSlide() {
      this.currentSlide = (this.currentSlide - 1 + this.productImages.length) % this.productImages.length;
    },
    goToSlide(index) {
      this.currentSlide = index;
    },
    startAutoplay() {
      this.autoplayInterval = setInterval(() => {
        if (!this.isAutoplayPaused) {
          this.nextSlide();
        }
      }, this.autoplayDelay);
    },
    stopAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
        this.autoplayInterval = null;
      }
    },
    pauseAutoplay() {
      this.isAutoplayPaused = true;
    },
    resumeAutoplay() {
      this.isAutoplayPaused = false;
    },
    
    // Pills
    togglePill(index) {
      this.activePill = this.activePill === index ? null : index;
    },
    async submitContactForm() {
      if (!this.$refs.contactForm.validate()) return;
      
      this.contactFormLoading = true;
      this.contactResult = null;
      try {
        const { data } = await api.post('/contact', this.contactForm);
        this.$refs.contactForm.reset();
        this.contactForm = { name: '', surname: '', email: '', message: '' };
        this.contactResult = { type: 'success', text: data.message };
      } catch (error) {
        this.contactResult = { type: 'error', text: error.response?.data?.message || 'No pudimos enviar tu mensaje. Escríbenos a contact@nfcero.com.' };
      } finally {
        this.contactFormLoading = false;
      }
    }
  }
}
</script>

<style scoped>
/* ===== Variables de Color ===== */
:root {
  --negro-base: #000000;
  --guinda: #69032E;
  --guinda-claro: #931B4D;
  --gris-suave: #C4C4C4;
  --gris-semi: #4C4C4C;
  --blanco: #FFFFFF;
}

/* ===== Estilos Globales ===== */
.home-wrapper {
  overflow-x: hidden;
  max-width: 100vw;
  width: 100%;
  position: relative;
}

.page-background {
  background-color: #000000;
  min-height: 100vh;
  overflow-x: hidden;
  max-width: 100vw;
}

.text-guinda {
  color: #76163E !important;
}

.text-guinda-claro {
  color: #931B4D !important;
}

/* ===== Hero Section ===== */
.hero-section {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  overflow: hidden;
}

.hero-section::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    linear-gradient(225deg, 
      rgba(105, 3, 46, 0.25) 0%,
      rgba(105, 3, 46, 0.1) 30%,
      rgba(0, 0, 0, 0.7) 70%,
      rgba(0, 0, 0, 0.9) 100%
    ),
    radial-gradient(ellipse at 50% 0%, rgba(147, 27, 77, 0.4) 0%, transparent 50%),
    radial-gradient(ellipse at 80% 20%, rgba(105, 3, 46, 0.3) 0%, transparent 40%);
  pointer-events: none;
  z-index: 0;
}

.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero-content-container {
  position: relative;
  z-index: 2;
  padding-top: 100px;
}

.hero-text-col {
  padding: 40px 20px;
}

.hero-title {
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 42px;
  line-height: 1.2;
  color: #FFFFFF;
  margin-bottom: 24px;
}

.hero-subtitle {
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 20px;
  line-height: 1.5;
  color: #FFFFFF;
  margin-bottom: 32px;
  max-width: 400px;
}

.hero-cta-btn {
  background-color: transparent !important;
  color: #FFFFFF !important;
  border: 2px solid #68213d !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 700 !important;
  padding: 12px 32px !important;
}

.hero-cta-btn:hover {
  background-color: #5c1d36 !important;
  color: #FFFFFF !important;
}

/* ===== About Section (Qué es NFCERO) ===== */
.about-section {
  background-color: #000000;
  padding: 80px 0;
}

.section-header {
  margin-bottom: 40px;
}

.section-pretitle {
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 20px;
  color: #76163E;
  margin-bottom: 8px;
}

.section-title {
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 35px;
  color: #FFFFFF;
  margin-bottom: 16px;
}

.section-subtitle {
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 18px;
  color: #FFFFFF;
  max-width: 500px;
  margin: 0 auto;
}

.about-description {
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 18px;
  line-height: 1.6;
  color: #FFFFFF;
  max-width: 900px;
  margin-bottom: 40px;
}

.feature-highlight {
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 20px;
  color: #931B4D;
  margin-bottom: 48px;
}

/* ===== Carrusel 3D Coverflow ===== */
.coverflow-container {
  width: 100%;
  padding: 40px 0 60px;
  overflow: hidden;
}

.coverflow-wrapper {
  perspective: 1200px;
  perspective-origin: center center;
}

.coverflow-stage {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  height: 350px;
  transform-style: preserve-3d;
}

.coverflow-item {
  position: absolute;
  width: 280px;
  height: 280px;
  border-radius: 16px;
  overflow: visible;
  cursor: pointer;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  transform-style: preserve-3d;
  backface-visibility: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
}

.coverflow-item.active {
  cursor: default;
  box-shadow: 0 20px 60px rgba(118, 22, 62, 0.4);
}

.coverflow-item:hover:not(.active) {
  opacity: 1 !important;
}

.coverflow-image {
  width: 100%;
  height: 100%;
  border-radius: 16px;
}

/* Texto debajo de las imágenes */
.coverflow-caption {
  position: absolute;
  bottom: -60px;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  color: #FFFFFF;
  font-size: 14px;
  font-weight: 500;
  margin: 0;
  padding: 0 10px;
  line-height: 1.4;
  width: 280px;
  z-index: 5;
}

/* Reflejo debajo de las imágenes */
.coverflow-item::after {
  content: '';
  position: absolute;
  bottom: -100%;
  left: 0;
  right: 0;
  height: 100%;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.3) 0%,
    transparent 50%
  );
  transform: scaleY(-1);
  opacity: 0.3;
  pointer-events: none;
  border-radius: 16px;
}

/* Navegación */
.coverflow-nav {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 100px;
}

.coverflow-btn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: rgba(118, 22, 62, 0.8);
  border: none;
  cursor: pointer;
  display: none;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.coverflow-btn:hover {
  background-color: #76163E;
  transform: scale(1.1);
}

.coverflow-dots {
  display: flex;
  gap: 10px;
}

.coverflow-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
}

.coverflow-dot.active {
  background-color: #76163E;
  transform: scale(1.3);
}

.coverflow-dot:hover:not(.active) {
  background-color: rgba(255, 255, 255, 0.6);
}

/* ===== Explore Section ===== */
.explore-section {
  background-color: #000000;
  padding: 80px 0;
}

/* ===== Explore Accordion ===== */
.explore-accordion-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  max-width: 420px;
  margin: 40px auto;
}

.explore-accordion-item {
  width: 100%;
  background-color: #000000;
  border: 1.5px solid #76163E;
  border-radius: 24px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.explore-accordion-item.active {
  border-radius: 24px;
}

.accordion-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.accordion-header:hover {
  background-color: rgba(118, 22, 62, 0.1);
}

.explore-accordion-item.active .accordion-header {
  background-color: rgba(118, 22, 62, 0.15);
}

.accordion-title {
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 18px;
  color: #FFFFFF;
}

.accordion-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #76163E;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
}

.explore-accordion-item.active .accordion-icon {
  background-color: #931B4D;
}

.accordion-content {
  padding: 0 24px 24px 24px;
  border-top: 1px solid rgba(118, 22, 62, 0.3);
}

.accordion-intro {
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #C4C4C4;
  margin: 16px 0 20px 0;
  line-height: 1.5;
}

.accordion-module {
  margin-bottom: 16px;
}

.accordion-module:last-child {
  margin-bottom: 0;
}

.accordion-module-title {
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 15px;
  color: #931B4D;
  margin-bottom: 4px;
}

.accordion-module-desc {
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #C4C4C4;
  margin: 0;
  line-height: 1.4;
}

/* ===== Networking Section ===== */
.networking-section {
  position: relative;
  height: 500px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow: hidden;
}

.networking-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
}

.networking-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.networking-content {
  position: relative;
  z-index: 1;
  padding-top: 60px;
}

.networking-title {
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 36px;
  color: #FFFFFF;
}

/* ===== FAQs Section ===== */
.faqs-section {
  background-color: #000000;
  padding: 80px 0;
}

.faqs-panels {
  max-width: 700px;
  margin: 40px auto 0;
}

.faq-panel {
  background-color: #000000 !important;
  border: 1.5px solid #76163E !important;
  border-radius: 24px !important;
  margin-bottom: 16px !important;
  overflow: hidden;
}

.faq-panel::before {
  display: none !important;
}

.faq-header {
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 16px;
  color: #FFFFFF;
  padding: 18px 24px !important;
  background-color: transparent !important;
}

.faq-header .v-expansion-panel-header__icon {
  color: #FFFFFF !important;
}

.faq-content {
  font-family: 'Inter', sans-serif;
  color: #c4c4c4;
  background-color: transparent !important;
  border-top: 1px solid rgba(118, 22, 62, 0.3);
  font-weight: 400;
  font-size: 14px;
  line-height: 1.6;
}

/* ===== Contact Section ===== */
.contact-section {
  background: 
    linear-gradient(45deg, 
      rgba(105, 3, 46, 0.25) 0%,
      rgba(105, 3, 46, 0.1) 30%,
      rgba(0, 0, 0, 1) 70%,
      rgba(0, 0, 0, 1) 100%
    ),
    #000000;
  position: relative;
  padding: 80px 0;
}

.contact-form-card {
  background-color: #000000 !important;
  border: 1.5px solid #76163E !important;
  border-radius: 24px !important;
  padding: 32px !important;
}

.contact-input {
  margin-bottom: 16px;
}

.contact-input.v-text-field--outlined .v-input__slot {
  background-color: transparent !important;
}

.contact-input.v-text-field--outlined legend {
  background-color: transparent !important;
}

.contact-input .v-label {
  color: #9D9D9D !important;
  background-color: #000000 !important;
  padding: 0 4px !important;
}

.contact-input input,
.contact-input textarea {
  color: #FFFFFF !important;
  caret-color: #FFFFFF !important;
}

.contact-input.v-text-field--outlined fieldset {
  border-color: rgba(118, 22, 62, 0.6) !important;
  border-radius: 12px !important;
}

.contact-input.v-text-field--outlined:hover fieldset {
  border-color: #931B4D !important;
}

.contact-input.v-text-field--outlined.v-input--is-focused fieldset {
  border-color: #931B4D !important;
  border-width: 2px !important;
}

.contact-input .v-text-field__details {
  padding-left: 12px !important;
}

.contact-input .v-messages__message {
  color: #9D9D9D !important;
}



.contact-submit-btn {
  background-color: #931B4D !important;
  color: #FFFFFF !important;
  border-radius: 24px !important;
  text-transform: none !important;
  font-weight: 500 !important;
  margin-top: 16px;
  margin-top: 16px;
}

/* ===== Responsive Styles ===== */
@media (max-width: 960px) {
  .hero-title {
    font-size: 32px;
  }

  .hero-subtitle {
    font-size: 18px;
  }

  .section-title {
    font-size: 28px;
  }

  .about-description {
    font-size: 16px;
    padding: 0 16px;
  }

  .coverflow-stage {
    height: 300px;
  }

  .coverflow-item {
    width: 220px;
    height: 220px;
  }

  .networking-section {
    height: 400px;
  }

  .networking-title {
    font-size: 28px;
  }
}

@media (max-width: 600px) {
  .hero-section {
    min-height: auto;
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }

  .hero-background {
    position: relative;
    width: 100%;
    height: 300px;
    order: -1;
  }

  .hero-image {
    width: 100%;
    height: 100%;
  }

  .hero-content-container {
    position: relative;
    z-index: 3;
    padding: 40px 20px;
    background: #000000;
    text-align: center;
  }

  .hero-text-col {
    padding: 0;
  }

  .hero-title {
    font-size: 28px;
    margin-bottom: 16px;
  }

  .hero-subtitle {
    font-size: 16px;
    margin-bottom: 24px;
  }

  .section-title {
    font-size: 24px;
  }

  .section-pretitle {
    font-size: 16px;
  }

  .about-section,
  .explore-section,
  .faqs-section,
  .contact-section {
    padding: 60px 0;
  }

  .coverflow-container {
    padding: 20px 0 40px;
  }

  .coverflow-stage {
    height: 250px;
  }

  .coverflow-item {
    width: 180px;
    height: 180px;
  }

  .coverflow-btn {
    width: 40px;
    height: 40px;
  }

  .explore-accordion-container {
    padding: 0 16px;
    max-width: 100%;
  }

  .accordion-title {
    font-size: 16px;
  }

  .accordion-icon {
    width: 36px;
    height: 36px;
  }

  .networking-section {
    height: 350px;
  }

  .networking-title {
    font-size: 24px;
  }

  .contact-form-card {
    padding: 24px !important;
    border-radius: 20px !important;
  }

  .faq-panel {
    border-radius: 20px !important;
  }

  .faq-header {
    font-size: 14px;
    padding: 16px 20px !important;
  }
}

</style>

<!-- Estilos para inputs del formulario (no scoped, limitados por #contact-section) -->
<style>
#contact-section .guinda-input.theme--light.v-text-field--outlined > .v-input__control > .v-input__slot fieldset {
  border-color: #931B4D !important;
  border-width: 1.5px !important;
  border-radius: 12px !important;
}

#contact-section .guinda-input.theme--light .v-label {
  color: #9D9D9D !important;
  background-color: #000000 !important;
}

#contact-section .guinda-input.theme--light input,
#contact-section .guinda-input.theme--light textarea {
  color: #FFFFFF !important;
}

/* Auditoría N8: en el carrusel solo se lee el texto de la imagen del centro (los laterales salían inclinados y diminutos) */
.coverflow-item:not(.active) .coverflow-caption {
  opacity: 0;
}
.coverflow-caption {
  transition: opacity 0.3s ease;
}
</style>
